<script setup>
import { ref } from '#imports';

defineProps({
  tags: {
    type: Object,
    required: true,
    default: () => Object,
  },
});

const emit = defineEmits(['fetchTag', 'search']);
const searchInput = ref(null);
const isInputFocused = ref(false);
const isInputOpened = ref(false);

const handleSearch = () => {
  emit('search', searchInput.value);
};

const handleInputFocus = () => {
  isInputFocused.value = !isInputFocused.value;
};
</script>

<template>
  <form class="search-aside" @submit.prevent="handleSearch">
    <div
      class="search-aside__wrapper"
      :class="{
        'search-aside__wrapper--focused': isInputFocused || searchInput,
        'search-aside__wrapper--opened': isInputOpened,
      }"
    >
      <div class="search-aside__icon"></div>
      <input
        v-model="searchInput"
        class="search-aside__input"
        type="search"
        placeholder="я ищу..."
        @focus="handleInputFocus"
        @blur="handleInputFocus"
        @keyup.enter="handleSearch"
      />
      <button
        class="search-aside__submit"
        :disabled="!searchInput"
        @click="searchInput && handleSearch"
      >
        Найти
      </button>
    </div>
  </form>
</template>

<style lang="postcss" scoped>
.search-aside {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  position: relative;

  &__icon {
    width: 28px;
    height: 28px;
    cursor: pointer;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    &::after {
      content: '';
      color: var(--accent-color);
      background-color: var(--white-color);
      -webkit-mask: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M17.4746 15.0885L13.5203 11.1341C14.2088 10.0192 14.625 8.71875 14.625 7.3125C14.625 3.27375 11.3513 0 7.3125 0C3.27375 0 0 3.27375 0 7.3125C0 11.3512 3.27375 14.625 7.3125 14.625C8.71875 14.625 10.0193 14.2087 11.133 13.5214L15.0885 17.4757C15.4068 17.7835 15.8334 17.9539 16.2762 17.9502C16.7189 17.9464 17.1425 17.7688 17.4556 17.4556C17.7686 17.1424 17.946 16.7187 17.9496 16.2759C17.9532 15.8331 17.7826 15.4067 17.4746 15.0885ZM7.3125 12.375C4.51688 12.375 2.25 10.1081 2.25 7.3125C2.25 4.51687 4.51688 2.25 7.3125 2.25C10.1081 2.25 12.375 4.51687 12.375 7.3125C12.375 10.1081 10.1081 12.375 7.3125 12.375Z' fill='white'/%3E%3C/svg%3E%0A");
      background-size: cover;
      background-repeat: no-repeat;
      width: 16px;
      height: 16px;
    }
  }

  &__input,
  &__button,
  &__submit {
    font-family: var(--title-font-family);
    font-size: 18px;
    line-height: 26px;
    transition: var(--default-transition) background-color;
    border: none;
    margin: 0;
    color: var(--accent-color);
  }

  &__input {
    background-color: transparent;
    width: 0;
    max-width: calc(100% - 62px);

    &:focus {
      outline: none;
      width: 100%;
    }
  }

  &__submit {
    opacity: 0;
    border-radius: 20px;
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    padding: 0 10px;
    margin: 2px;
    background-color: var(--green-color);
    color: var(--accent-color);
    font-size: 16px;
    line-height: 24px;

    &:hover {
      cursor: pointer;
      background-color: var(--green-color);
      color: var(--accent-color);
    }
  }

  &__submit:disabled {
    background-color: var(--accent-color);
    color: var(--white-color);
    cursor: default;
  }

  &__wrapper {
    display: flex;
    flex-direction: row;
    border-radius: 20px;
    background-color: var(--accent-color);
    color: #fffafd;
    width: 60px;
    padding: 6px 16px;
    box-shadow: 0 0 0 2px var(--accent-color) inset;
    transition: width ease 0.3s;

    &--focused {
      transition: width 0.3s ease-in-out;
      max-width: 290px;
      width: 100%;
      box-shadow: 0 0 0 2px var(--accent-color) inset;
      background-color: transparent;

      .search-aside__input {
        width: 100%;
      }

      .search-aside__icon {
        display: none;
      }

      .search-aside__submit {
        opacity: 1;
      }

      .search-aside__icon::after {
        background-color: var(--accent-color);
      }
    }

    &:hover {
      box-shadow: 0 0 0 2px var(--accent-color) inset;
      background-color: transparent;
      max-width: 290px;
      width: 100%;

      .search-aside__icon {
        display: none;
      }

      .search-aside__input {
        width: 100%;
      }

      .search-aside__submit {
        opacity: 1;
        transition: all 0.3s ease-in-out;
        transition-delay: 0.1s;
      }

      .search-aside__icon::after {
        background-color: var(--accent-color);
      }
    }
  }

  input[type='search']::-webkit-textfield-decoration-container {
    width: calc(100% - 5px);
  }

  input[type='search']::-webkit-search-cancel-button {
    -webkit-appearance: none;
    cursor: pointer;
    height: 12px;
    width: 12px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'%3E%3Cpath d='M12 0.746398C12 0.546068 11.922 0.357648 11.7803 0.216126C11.4968 -0.0673219 11.0032 -0.0673219 10.7197 0.216126L6 4.93585L1.28027 0.216126C0.996824 -0.0673219 0.503172 -0.0673219 0.219724 0.216126C0.0780155 0.357648 0 0.546072 0 0.746398C0 0.946705 0.0780155 1.13512 0.219728 1.27667L4.93946 5.9964L0.219728 10.7161C0.0780155 10.8576 0 11.0461 0 11.2464C0 11.4467 0.0780155 11.6351 0.219728 11.7767C0.503175 12.0601 0.996828 12.0601 1.28028 11.7767L6 7.05695L10.7197 11.7767C10.8615 11.9184 11.0497 11.9964 11.25 11.9964C11.4503 11.9964 11.6386 11.9184 11.7803 11.7767C11.922 11.6351 12 11.4467 12 11.2464C12 11.0461 11.922 10.8576 11.7803 10.7161L7.06055 5.9964L11.7803 1.27667C11.922 1.13513 12 0.946705 12 0.746398Z' fill='%23682d72'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    margin-left: 5px;
  }
}
</style>
